import React from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";

/**
 * Secondary security warning for root/jailbreak detection via JS layer.
 * Primary checks (root, Frida, developer options) are handled natively
 * in MainActivity.onCreate before the WebView loads.
 */
const SecurityWarning = ({ securityStatus, blockAccess = false }) => {
    if (!Capacitor.isNativePlatform() || !securityStatus || securityStatus.isSecure) {
        return null;
    }

    const handleExit = async () => {
        if (Capacitor.isNativePlatform()) {
            await CapApp.exitApp();
        }
    };

    const issues = [];
    const fixes = [];

    if (securityStatus.status?.isRooted) {
        issues.push("Rooted/Jailbroken device detected");
        fixes.push("This app cannot run on rooted or jailbroken devices. Please use an unmodified device.");
    }
    if (securityStatus.status?.isEmulator) {
        issues.push("Running on emulator");
        fixes.push("This app cannot run on emulators. Please use a physical device.");
    }
    if (securityStatus.status?.hasRootApps) {
        issues.push("Root management apps detected");
        fixes.push("Please uninstall root management apps (e.g. Magisk, SuperSU) from your device.");
    }
    if (securityStatus.status?.hasSuBinary) {
        issues.push("SU binary detected");
        fixes.push("Your device has root access enabled. Please use an unrooted device.");
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl overflow-y-auto max-h-[90vh]">
                <div className="flex items-center mb-4">
                    <div className="flex-shrink-0">
                        <svg
                            className="h-8 w-8 text-red-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                    <h2 className="ml-3 text-xl font-semibold text-gray-900">
                        Security Warning
                    </h2>
                </div>

                <div className="mb-4">
                    <p className="text-gray-700 mb-3">
                        This app cannot run on compromised devices for security reasons.
                    </p>

                    <div className="space-y-3">
                        {issues.map((issue, index) => (
                            <div key={index} className="bg-red-50 border border-red-200 rounded-md p-3">
                                <p className="text-sm font-medium text-red-800">{issue}</p>
                                {fixes[index] && (
                                    <p className="text-xs text-red-600 mt-1">{fixes[index]}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={handleExit}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                        Exit App
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SecurityWarning;
